import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';

// Define the shape of our User based on the Fastify API response
export interface AuthUser {
  id: string;
  name: string;
  email: string;
  tier: 'FREE' | 'PRO' | 'TEAM';
  storageUsed: number;
  organizationId?: string | null;
  orgRole?: 'OWNER' | 'ADMIN' | 'MEMBER' | 'BILLING' | null;
}

// Define the context state
interface AuthContextState {
  user: AuthUser | null;
  token: string | null;
  isLoading: boolean;
  login: (token: string, user: AuthUser) => void;
  logout: () => void;
  updateUser: (updates: Partial<AuthUser>) => void;
}

// Initialize with a default structure
const AuthContext = createContext<AuthContextState | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const logout = useCallback(() => {
    setToken(null);
    setUser(null);
    localStorage.removeItem('ehxcel_token');
    localStorage.removeItem('ehxcel_user');
  }, []);

  // Rehydrate session from localStorage on mount
  useEffect(() => {
    const storedToken = localStorage.getItem('ehxcel_token');
    const storedUser = localStorage.getItem('ehxcel_user');

    if (storedToken && storedUser) {
      try {
        // Decode the JWT payload to proactively check expiration
        const payloadBase64 = storedToken.split('.')[1];
        const decodedJson = atob(payloadBase64);
        const decoded = JSON.parse(decodedJson);
        
        if (decoded.exp && decoded.exp * 1000 < Date.now()) {
          throw new Error('Token expired');
        }

        setToken(storedToken);
        setUser(JSON.parse(storedUser));
      } catch (error) {
        console.error('Failed to parse stored user data or token expired', error);
        localStorage.removeItem('ehxcel_token');
        localStorage.removeItem('ehxcel_user');
      }
    }
    setIsLoading(false);
  }, []);

  // Listen for global 401 unauthorized events
  useEffect(() => {
    const handleUnauthorized = () => {
      logout();
    };
    window.addEventListener('auth:unauthorized', handleUnauthorized);
    return () => window.removeEventListener('auth:unauthorized', handleUnauthorized);
  }, [logout]);

  const login = (newToken: string, newUser: AuthUser) => {
    setToken(newToken);
    setUser(newUser);
    localStorage.setItem('ehxcel_token', newToken);
    localStorage.setItem('ehxcel_user', JSON.stringify(newUser));
  };

  const updateUser = (updates: Partial<AuthUser>) => {
    if (!user) return;
    const updatedUser = { ...user, ...updates };
    setUser(updatedUser);
    localStorage.setItem('ehxcel_user', JSON.stringify(updatedUser));
  };

  return (
    <AuthContext.Provider value={{ user, token, isLoading, login, logout, updateUser }}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook for strict-typed consumption
export const useAuth = (): AuthContextState => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};